import { useCallback, useState } from 'react';

import { usePagesStore } from '@/stores/pages-store';
import type { Page } from '@/types/page';
import { buildPagesTree } from './page-utils';
import { getPublishParentWarning, getUnpublishWarningCount } from './page-visibility-warning-utils';

export type PageVisibilityDropdownWarning =
  | { type: 'publish' }
  | { type: 'unpublish'; subpageCount: number };

export function usePageVisibilityDropdownWarnings(
  page: Page,
  {
    onPublish,
    onUnpublish,
  }: {
    onPublish: () => void | Promise<void>;
    onUnpublish: () => void | Promise<void>;
  },
) {
  const [warning, setWarning] = useState<PageVisibilityDropdownWarning | null>(null);

  const requestPublish = useCallback(() => {
    if (!page.id) return;

    const state = usePagesStore.getState();
    const hasUnpublishedParent = !!getPublishParentWarning(
      [...buildPagesTree(state.mainNavigationFlat), ...buildPagesTree(state.nonNavigationFlat)],
      page.id,
    );

    if (hasUnpublishedParent) {
      setWarning({ type: 'publish' });
      return;
    }

    void onPublish();
  }, [page.id, onPublish]);

  const requestUnpublish = useCallback(() => {
    const state = usePagesStore.getState();
    const subpageCount = getUnpublishWarningCount(
      [...buildPagesTree(state.mainNavigationFlat), ...buildPagesTree(state.nonNavigationFlat)],
      page,
    );

    if (subpageCount > 0) {
      setWarning({ type: 'unpublish', subpageCount });
      return;
    }

    void onUnpublish();
  }, [page, onUnpublish]);

  const confirmWarning = useCallback(async () => {
    if (!warning) return;

    if (warning.type === 'publish') {
      await onPublish();
    } else {
      await onUnpublish();
    }
  }, [warning, onPublish, onUnpublish]);

  return {
    warning,
    setWarning,
    requestPublish,
    requestUnpublish,
    confirmWarning,
  };
}
